import {
  Button,
  type CSS,
  type IconNames,
  StyledMultiSelectV2 as Select,
  styled,
} from '@kandji-inc/nectar-ui';
import { i18n } from 'i18n';
import * as React from 'react';
import { useGetEnabledPlatforms } from 'src/hooks/useGetEnabledPlatforms';
import type {
  DeviceFilter,
  DeviceSelectValues,
} from './DeviceFamilyFilter.types';
import type { DeviceFilterItemProps } from './DeviceFilterItem';
import { DeviceFilterTriggers } from './DeviceFilterTriggers';
import { DeviceFilterTransformers } from './utils/DeviceFilterTransformers';
import { findAndUpdateFilter } from './utils/findAndUpdateFilter';

export const ClearButton = styled('div', {
  color: '#35414E7A',
  fontWeight: 500,
  fontSize: 14,
});

/**
 * DeviceFamilyFilter component allows users to filter devices by their families nested by
 * platforms (e.g., Apple, Android, Windows).
 *
 * Note: This component is generally generic but is currently only used for device families. If we
 * decide to use it for other types of filters, we should move it somewhere more generic.
 */

interface DeviceFamilyFilterProps {
  onChange: (filters: DeviceFilter[]) => void;
  onClear: () => void;
  label?: string;
  filters?: DeviceFilter[];
  trigger?: (props: any) => React.ReactNode;
  filterItem?: (props: DeviceFilterItemProps) => React.ReactNode;
  disabled?: boolean;
  counterTooltip?: {
    content: React.ReactNode;
    icon?: IconNames;
    color?: string;
    side?: 'top' | 'bottom';
  };
  menuCss?: CSS;
  menuFooterCss?: CSS;
}

/* istanbul ignore next */
export function DeviceFamilyFilter({
  onChange,
  onClear,
  label = i18n.t('Device'),
  filters = [],
  trigger,
  filterItem,
  disabled,
  counterTooltip,
  menuCss = {},
  menuFooterCss = {},
}: DeviceFamilyFilterProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const triggerRef = React.useRef();

  const updateFilters = React.useCallback(
    (id: string, value: DeviceSelectValues) => {
      const newFilters = findAndUpdateFilter(filters, id, { value });
      onChange(newFilters);
    },
    [onChange, filters],
  );

  const deviceFilters = React.useMemo(
    () =>
      DeviceFilterTransformers.toDeviceFilterItems({
        filters,
        handlers: { updateFilters },
        component: filterItem,
      }),
    [filters, updateFilters, filterItem],
  );

  const filterLabels = React.useMemo(
    () => DeviceFilterTransformers.toLabelArray(filters),
    [filters],
  );

  const filtersApplied = filterLabels.length > 0;

  const Trigger = trigger != null ? trigger : DeviceFilterTriggers.Default;

  return (
    <Select.Root
      aria-label={i18n.t('device family filter')}
      isOpen={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
      }}
    >
      <Trigger
        ref={triggerRef}
        filters={filters}
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        updateFilters={updateFilters}
        label={label}
        filterLabels={filterLabels}
        filtersApplied={filtersApplied}
        disabled={disabled}
        counterTooltip={counterTooltip}
      />

      <Select.Popover
        offset={4}
        css={{
          minWidth: 'var(--trigger-width)',
          width: 'var(--dff-popover-width, 300px)',
          ...menuCss,
        }}
        triggerRef={triggerRef}
      >
        <Select.ListBox
          selectionMode="multiple"
          selectionBehavior="toggle"
          items={filters}
        >
          <>
            {deviceFilters}
            <Select.ListBoxItem
              textValue={i18n.t('Clear')}
              data-testid="device-family-clear"
              onAction={() => {
                onClear();
              }}
              css={menuFooterCss}
            >
              <Button
                css={{
                  padding: 0,
                  background: 'none',
                  '&:hover': { background: 'none' },
                }}
                compact
                variant="subtle"
                disabled={!filtersApplied}
              >
                {i18n.t('Clear')}
              </Button>
            </Select.ListBoxItem>
          </>
        </Select.ListBox>
      </Select.Popover>
    </Select.Root>
  );
}

export const DeviceFamilyFilterWrapper = React.memo(
  function DeviceFamilyFilterWrapper({
    fallback,
    ...props
  }: {
    fallback: React.ReactNode;
  } & DeviceFamilyFilterProps) {
    const { crossPlatformEnabled } = useGetEnabledPlatforms();

    if (!crossPlatformEnabled) {
      return fallback;
    }

    return <DeviceFamilyFilter {...props} />;
  },
);
